import { Icon, Modal, SegmentedCard, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  FEATURE_TYPES,
  RECOGNITION_CREATION_STEPS,
  AUTOMATION_CREATION_STEPS,
  FLOW_CODES
} from '@hero/constants';
import { useIsAutomationEnabled } from '@hero/hooks';
import { ROUTE_PATHS } from '@hero/routes';
import { useCreateAutomation, useCreateRecognition } from '@hero/services';
import { useNewAutomationStore, useNewRecognitionStore, useShoppingCart } from '@hero/store/hooks';
import { automationCreationSchema, getNameSchema } from '@hero/validations';

import { CreateAutomationForm } from '../CreateAutomationForm';
import { CreateRecognitionForm } from '../CreateRecognitionForm';

import * as S from './style';

function ChooseFeatureTypeModal({
  initialProducts = [],
  setInitialProducts = () => {},
  isOpen,
  onClose,
  redirectToGiftStep = false
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: createAutomation, isLoading: isCreateAutomationLoading } =
    useCreateAutomation();
  const { mutateAsync: createRecognition, isLoading: isCreateRecognitionLoading } =
    useCreateRecognition();
  const isAutomationEnabled = useIsAutomationEnabled();

  const {
    updateType: updateAutomationType,
    updateName: updateAutomationName,
    updateProducts: updateAutomationProducts,
    updateCode: updateAutomationCode,
    reset: resetAutomation
  } = useNewAutomationStore();
  const {
    updateName: updateRecognitionName,
    updateProducts: updateRecognitionProducts,
    reset: resetRecognition,
    updateCode: updateRecognitionCode
  } = useNewRecognitionStore();

  const { products, updateProducts } = useShoppingCart();

  const [activeFeatureType, setActiveFeatureType] = useState(null);

  const automationFormik = useFormik({
    initialValues: {
      name: '',
      type: ''
    },
    validateOnMount: true,
    validationSchema: automationCreationSchema,
    async onSubmit(values) {
      const redirectToRules = (initialProducts.length || products.length) && !redirectToGiftStep;
      resetAutomation();
      updateAutomationName(values.name);
      updateAutomationType(values.type);
      updateAutomationProducts(initialProducts.length ? initialProducts : products);
      const { code } = await createAutomation({
        name: values.name,
        products: initialProducts.length ? initialProducts : products,
        type: values.type,
        meta: {
          creationStep: redirectToRules
            ? AUTOMATION_CREATION_STEPS.newAutomationRules
            : AUTOMATION_CREATION_STEPS.newAutomationGifts
        }
      });
      updateAutomationCode(code);
      updateProducts([]);
      setInitialProducts([]);
      if (redirectToRules) {
        navigate(ROUTE_PATHS.NEW_AUTOMATION_RULES);
      } else {
        navigate(ROUTE_PATHS.NEW_AUTOMATION_GIFTS);
      }
    }
  });

  const recognitionFormik = useFormik({
    initialValues: {
      name: ''
    },
    validateOnMount: true,
    validationSchema: getNameSchema(),
    async onSubmit(values) {
      const redirectToContactType =
        (initialProducts.length || products.length) && !redirectToGiftStep;
      resetRecognition();
      updateRecognitionName(values.name);
      updateRecognitionProducts(initialProducts.length ? initialProducts : products);
      const { code } = await createRecognition({
        name: values.name,
        products: initialProducts.length ? initialProducts : products,
        meta: {
          creationStep: redirectToContactType
            ? RECOGNITION_CREATION_STEPS.newRecognitionContactType
            : RECOGNITION_CREATION_STEPS.newRecognitionGifts
        },
        flowCode: FLOW_CODES.standard
      });

      updateRecognitionCode(code);
      updateProducts([]);
      setInitialProducts([]);
      if (redirectToContactType) {
        navigate(ROUTE_PATHS.NEW_RECOGNITION_CONTACT_TYPE);
      } else {
        navigate(ROUTE_PATHS.NEW_RECOGNITION_GIFTS);
      }
    }
  });

  const featureOptions = useMemo(() => {
    const options = [
      {
        icon: <Icon name="gift-box" size="xxl" />,
        title: t('oneTimeGifts'),
        value: FEATURE_TYPES.recognition,
        hiddenInput: true,
        description: <Typography variant="body2">{t('oneTimeGiftDesc')}</Typography>
      }
    ];

    if (isAutomationEnabled) {
      options.push({
        icon: <Icon name="recurring-gift" size="xxl" />,
        title: t('recurringGifts'),
        value: FEATURE_TYPES.automation,
        hiddenInput: true,
        description: <Typography variant="body2">{t('recurringGiftDesc')}</Typography>
      });
    }

    return options;
  }, [t]);
  const Content = (
    <S.ChooseFeatureType>
      <Typography variant="subtitle4Bold" style={{ textAlign: 'center' }}>
        {t('howYouLikeToSendThisGift')}
      </Typography>
      <S.FeatureTypeWrapper>
        <SegmentedCard
          updateHash={false}
          activeTab={activeFeatureType}
          onChange={setActiveFeatureType}
          options={featureOptions}
        />
      </S.FeatureTypeWrapper>
      {FEATURE_TYPES.recognition === activeFeatureType && (
        <CreateRecognitionForm formik={recognitionFormik} />
      )}
      {FEATURE_TYPES.automation === activeFeatureType && (
        <CreateAutomationForm formik={automationFormik} />
      )}
    </S.ChooseFeatureType>
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      size={isAutomationEnabled ? 'm' : 'sm'}
      title={t('chooseGiftingMethod')}
      primaryButtonProps={{
        children: t('next'),
        onClick: () => {
          if (activeFeatureType === FEATURE_TYPES.recognition) recognitionFormik.submitForm();
          else automationFormik.submitForm();
        },
        disabled:
          activeFeatureType === FEATURE_TYPES.recognition
            ? !recognitionFormik.isValid
            : !automationFormik.isValid,
        loading: isCreateRecognitionLoading || isCreateAutomationLoading,
        endIcon: <Icon name="next" />
      }}
      secondaryButtonProps={{
        children: t('cancel')
      }}
    >
      {Content}
    </Modal>
  );
}

export { ChooseFeatureTypeModal };
